import { Fragment, useEffect, useState } from 'react';
import { getVariationFillColor } from '@gonfalon/flag-variations/getVariationFillColor';
import { VariationColor } from '@gonfalon/flag-variations/VariationColor';
import type { schemas } from '@gonfalon/openapi/types';
import type { DirectionValue, DirectionValueObject, GapValue, GapValueObject } from '@gonfalon/ui/grid';
import { Cell, CellLayoutValue, Grid } from '@gonfalon/ui/grid';
import { Focusable, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import cx from 'clsx';

import styles from './Rollout.module.css';

type Variation = schemas['Variation'];
type VariationWithTooltip = Variation & { tooltip?: string };

const MAX_WIDTH_HORIZONTAL = 645;

/** Number of cohorts a migration flag has — fixes the variation count and progression layout. */
export type StageCount = 2 | 4 | 6;

/**
 * Native rollout configuration consumed by the read-only view. Pass to render
 * percentages, progress bars, and the "By <kind> <attr>" bucketing label;
 * omit to render variation chips only (no rollout summary).
 */
export interface RolloutData {
  /** Per-variation weight in basis points (0–100000), index-aligned with `variations`. `null` means no weight is set for that index. */
  weights: ReadonlyArray<number | null>;
  /** Context kind used for bucketing. */
  contextKind: string;
  /** Attribute used for bucketing within the context kind. Falls back to `'key'` when omitted. */
  bucketBy?: string;
}

export interface RolloutProps {
  variations: VariationWithTooltip[];
  stageCount: StageCount;
  rollout?: RolloutData;
}

/**
 * Read-only view of a migration-flag rollout: variation chips with names, per-stage
 * percentage labels, progress bars, and arrows showing stage progression order. Used
 * by both the legacy targeting page (via a thin adapter in `static/ld`) and—future
 * PR—the rewrite's migration sibling.
 */
export function Rollout({ variations, stageCount, rollout }: RolloutProps) {
  const [isHorizontal, setIsHorizontal] = useState(window.innerWidth > MAX_WIDTH_HORIZONTAL);
  const hasSixStages = stageCount === 6;

  useEffect(() => {
    function handleResize() {
      setIsHorizontal(window.innerWidth > MAX_WIDTH_HORIZONTAL);
    }
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderRolloutPercentage = (index: number, name?: string) => {
    if (rollout) {
      const rolloutVariation = rollout.weights[index];
      if (rolloutVariation !== null && rolloutVariation !== undefined) {
        const percentage = `${rolloutVariation / 1000}%`;
        return (
          <h3 className={styles.percentageTitle} data-test-id={`${name}-${percentage}`}>
            {percentage}
          </h3>
        );
      }
    }
    return null;
  };

  const calculateBorderRadii = (percentage: number) => {
    switch (true) {
      case percentage === 100:
        return '0px 0px var(--lp-border-radius-medium) var(--lp-border-radius-medium)';
      case percentage < 3:
        return '0px 0px 0px 50px';
      default:
        return '0px 0px 0px var(--lp-border-radius-medium)';
    }
  };

  const renderProgressBar = (index: number) => {
    if (rollout) {
      const rolloutVariation = rollout.weights[index];
      if (rolloutVariation !== null && rolloutVariation !== undefined) {
        const percentage = rolloutVariation / 1000;
        const fillerStyles = {
          width: `${percentage}%`,
          backgroundColor: getVariationFillColor(index),
          height: '100%',
          borderRadius: calculateBorderRadii(percentage),
        };
        return (
          <div className={styles.progressBar}>
            {rolloutVariation !== 0 && (
              <div className={styles.progressContainer}>
                <div aria-valuenow={percentage} style={fillerStyles}></div>
              </div>
            )}
          </div>
        );
      }
    }
  };

  const renderArrow = (index: number) =>
    index === variations.length - 1 ? (
      <></>
    ) : (
      <div className={cx(styles.arrow, { [styles.sixStageArrow]: hasSixStages })}>
        <Icon name={!isHorizontal && hasSixStages ? 'arrow-down-thin' : 'arrow-right-thin'} size="small" />
      </div>
    );

  const render = (stage: StageCount) => {
    if (!stage) {
      return <></>;
    }

    let gap: GapValue | GapValueObject = '1';
    let direction: DirectionValue | DirectionValueObject | undefined;
    switch (stage) {
      case 6:
        gap = { mobile: '0', desktop: '1' };
        direction = isHorizontal ? 'row' : 'column';
        break;
      default:
        break;
    }

    return (
      <>
        <Grid gap={gap} direction={direction} className={styles.container} data-test-id="rollout">
          {variations.map((variation, index) => (
            <Fragment key={variation._id}>
              <TooltipTrigger>
                <Focusable>
                  <span role="button" className={styles.tooltip}>
                    <Cell layout={CellLayoutValue.TWELVE_OF_TWELVE}>
                      <div className={styles.variationGroup}>
                        <div
                          className={cx(styles.variationName, {
                            [styles.variationNameWithRollout]: !!rollout,
                          })}
                        >
                          <VariationColor variationIndex={index} />
                          <p>{variation.name}</p>
                        </div>
                        {renderRolloutPercentage(index, variation.name)}
                      </div>
                      {renderProgressBar(index)}
                    </Cell>
                  </span>
                </Focusable>
                <Tooltip placement="top">{variation.description}</Tooltip>
              </TooltipTrigger>
              {renderArrow(index)}
            </Fragment>
          ))}
        </Grid>
        {rollout && (
          <Grid>
            <Cell>
              By <code>{rollout.contextKind}</code> <code>{rollout.bucketBy || 'key'}</code>
            </Cell>
          </Grid>
        )}
      </>
    );
  };

  return render(stageCount);
}
